/**
 * Active Calls Monitor Page
 * Real-time view of all active/queued calls with management capabilities
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { Phone, Clock, XCircle, AlertCircle, RefreshCw, Trash2 } from 'lucide-react';

interface ActiveCall {
  id: string;
  status: string;
  queue_name: string | null;
  direction: string;
  started_at: string;
  ended_at: string | null;
  customer_phone: string | null;
  agent_id: string | null;
  twilio_call_sid: string | null;
}

export default function ActiveCallsMonitor() {
  const queryClient = useQueryClient();
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterQueue, setFilterQueue] = useState<string>('all');

  // Fetch active calls
  const { data: activeCalls, isLoading, refetch } = useQuery({
    queryKey: ['active-calls', filterStatus, filterQueue],
    queryFn: async () => {
      let query = supabase
        .from('calls')
        .select('*')
        .is('ended_at', null)
        .order('started_at', { ascending: false });

      // Apply status filter
      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      } else {
        query = query.in('status', ['queued', 'ringing', 'in-progress', 'initiated']);
      }

      // Apply queue filter
      if (filterQueue !== 'all') {
        query = query.eq('queue_name', filterQueue);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as ActiveCall[];
    },
    refetchInterval: 5000, // Auto-refresh every 5 seconds
  });

  // Get unique queue names for filter
  const queueNames = React.useMemo(() => {
    if (!activeCalls) return [];
    const names = new Set(activeCalls.map(c => c.queue_name).filter(Boolean));
    return Array.from(names);
  }, [activeCalls]);

  // Mutation to end a single call
  const endCallMutation = useMutation({
    mutationFn: async (callId: string) => {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('calls')
        .update({
          status: 'completed',
          ended_at: now,
          updated_at: now,
        })
        .eq('id', callId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-calls'] });
    },
  });

  // Mutation to end stale calls (>1 hour old)
  const endStaleCallsMutation = useMutation({
    mutationFn: async () => {
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const now = new Date().toISOString();
      
      const { error } = await supabase
        .from('calls')
        .update({
          status: 'completed',
          ended_at: now,
          updated_at: now,
        })
        .is('ended_at', null)
        .lt('started_at', oneHourAgo)
        .in('status', ['queued', 'ringing', 'in-progress', 'initiated']);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-calls'] });
    },
  });

  // Calculate wait time in minutes
  const getWaitTime = (startedAt: string) => {
    const start = new Date(startedAt).getTime();
    const now = Date.now();
    return Math.round((now - start) / 60000);
  };

  // Format wait time display
  const formatWaitTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < 24) {
      return `${hours}h ${mins}m`;
    }
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'queued': return 'bg-yellow-100 text-yellow-800';
      case 'ringing': return 'bg-blue-100 text-blue-800';
      case 'in-progress': return 'bg-green-100 text-green-800';
      case 'initiated': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Identify stale calls (>1 hour old)
  const staleCalls = activeCalls?.filter(call => getWaitTime(call.started_at) > 60) || [];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Phone className="w-6 h-6" />
              Active Calls Monitor
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Real-time view of all active and queued calls
            </p>
          </div>
          
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Total Active</div>
            <div className="text-2xl font-bold text-gray-900">{activeCalls?.length || 0}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Queued</div>
            <div className="text-2xl font-bold text-yellow-600">
              {activeCalls?.filter(c => c.status === 'queued').length || 0}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">In Progress</div>
            <div className="text-2xl font-bold text-green-600">
              {activeCalls?.filter(c => c.status === 'in-progress').length || 0}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Stale (&gt;1h)</div>
            <div className="text-2xl font-bold text-red-600">{staleCalls.length}</div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">All</option>
                <option value="queued">Queued</option>
                <option value="ringing">Ringing</option>
                <option value="in-progress">In Progress</option>
                <option value="initiated">Initiated</option>
              </select>
            </div>

            {/* Queue Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Queue:</label>
              <select
                value={filterQueue}
                onChange={(e) => setFilterQueue(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">All Queues</option>
                {queueNames.map(name => (
                  <option key={name} value={name || ''}>{name || 'No Queue'}</option>
                ))}
              </select>
            </div>

            {/* Cleanup Stale Calls Button */}
            {staleCalls.length > 0 && (
              <button
                onClick={() => {
                  if (confirm(`End ${staleCalls.length} stale call(s) older than 1 hour?`)) {
                    endStaleCallsMutation.mutate();
                  }
                }}
                disabled={endStaleCallsMutation.isPending}
                className="ml-auto flex items-center gap-2 px-4 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                End {staleCalls.length} Stale Call{staleCalls.length !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>

        {/* Stale Calls Warning */}
        {staleCalls.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-900">Stale Calls Detected</h3>
                <p className="text-sm text-red-700 mt-1">
                  {staleCalls.length} call{staleCalls.length !== 1 ? 's' : ''} ha{staleCalls.length !== 1 ? 've' : 's'} been active for over 1 hour. 
                  These may be stuck calls that should be ended.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Calls List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">
            <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
            Loading active calls...
          </div>
        ) : activeCalls && activeCalls.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Call ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Queue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Direction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Wait Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Started
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activeCalls.map((call) => {
                  const waitMinutes = getWaitTime(call.started_at);
                  const isStale = waitMinutes > 60;
                  
                  return (
                    <tr key={call.id} className={isStale ? 'bg-red-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                        {call.id.substring(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(call.status)}`}>
                          {call.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {call.queue_name || <span className="text-gray-400">No Queue</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`capitalize ${call.direction === 'inbound' ? 'text-blue-600' : 'text-green-600'}`}>
                          {call.direction}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Clock className={`w-4 h-4 ${isStale ? 'text-red-600' : 'text-gray-400'}`} />
                          <span className={`text-sm font-medium ${isStale ? 'text-red-600' : 'text-gray-900'}`}>
                            {formatWaitTime(waitMinutes)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {call.customer_phone || <span className="text-gray-400">N/A</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(call.started_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => {
                            if (confirm('End this call?')) {
                              endCallMutation.mutate(call.id);
                            }
                          }}
                          disabled={endCallMutation.isPending}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          <XCircle className="w-4 h-4" />
                          End Call
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <Phone className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Calls</h3>
            <p className="text-gray-500">
              {filterStatus !== 'all' || filterQueue !== 'all' 
                ? 'No calls match the current filters'
                : 'All calls have been completed'}
            </p>
          </div>
        )}
      </div>

      {/* Auto-refresh indicator */}
      <div className="mt-4 text-center text-sm text-gray-500">
        Auto-refreshing every 5 seconds • Last updated: {new Date().toLocaleTimeString()}
      </div>
    </div>
  );
}
